import React, { useState, useRef, useEffect } from 'react';
import BoxRow from '../components/shared-mass/BoxRow';
import useSharedMass from "../hooks/useSharedMass";
import { Search, RotateCcw, Copy, Check, Filter, LayoutGrid, Loader2, GripVertical } from 'lucide-react';

const SharedMassPage = () => {
  // Hook
  const { accounts: fetchedAccounts, isLoading, error } = useSharedMass(); 
  
  // States
  const [accounts, setAccounts] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalFolder, setGlobalFolder] = useState("ALL");
  const [globalCopied, setGlobalCopied] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  
  const boxRefs = useRef([]); 

  // Sync fetched accounts with local state and localStorage order
  useEffect(() => {
    if (fetchedAccounts && fetchedAccounts.length > 0) {
      const savedOrder = localStorage.getItem('shared_mass_order');
      let sorted = [...fetchedAccounts];
      
      if (savedOrder) {
        const orderArray = JSON.parse(savedOrder);
        sorted.sort((a, b) => {
          const indexA = orderArray.indexOf(a.email);
          const indexB = orderArray.indexOf(b.email);
          
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      }
      setAccounts(sorted);
    }
  }, [fetchedAccounts]);

  const saveOrder = (newAccounts) => {
    const orderArray = newAccounts.map(acc => acc.email);
    localStorage.setItem('shared_mass_order', JSON.stringify(orderArray));
  };

  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Add a ghost image or styling if needed
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItemIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;

    const newAccounts = [...accounts];
    const draggedItem = newAccounts[draggedItemIndex];
    
    // Remove from old position and insert at new position
    newAccounts.splice(draggedItemIndex, 1);
    newAccounts.splice(targetIndex, 0, draggedItem);
    
    setAccounts(newAccounts);
    saveOrder(newAccounts);
  };

  const handleRefreshAll = () => {
    boxRefs.current.forEach(ref => {
        if (ref) ref.triggerRefresh(); 
    });
};

  const handleCopyAllIps = () => {
      let allIps = [];
      boxRefs.current.forEach(ref => {
          if (ref) {
              const ips = ref.getVisibleIps(); 
              allIps = [...allIps, ...ips];
          }
      });
      const uniqueIps = [...new Set(allIps)];
      if (uniqueIps.length > 0) {
          navigator.clipboard.writeText(uniqueIps.join('\n'));
          setGlobalCopied(true);
          setTimeout(() => setGlobalCopied(false), 2000);
      }
  };

  return (
    <div className="container-fluid bg-light min-vh-100 p-0">
      
      {/* --- HEADER WITH FILTERS --- */}
      <div className="sticky-top bg-white border-bottom shadow-sm px-3 py-2 d-flex align-items-center justify-content-between" style={{zIndex: 1000, height: '60px'}}>
          
          <div className="d-flex align-items-center gap-2">
              <div className="bg-primary bg-opacity-10 p-2 rounded">
                 <LayoutGrid size={18} className="text-primary" />
              </div>
              <h6 className="m-0 fw-bold text-dark" style={{fontSize: '15px'}}>Shared Mass</h6>
              <span className="badge bg-secondary bg-opacity-10 text-secondary rounded-pill border ms-1">
                {isLoading && accounts.length === 0 ? <Loader2 size={12} className="spin-animation" /> : accounts.length} Boxes
              </span>
          </div>

          <div className="d-flex align-items-center gap-2">
              {/* Filter (Inbox/Spam) */}
              <div className="position-relative">
                  <Filter size={14} className="text-muted position-absolute" style={{left: '10px', top: '50%', transform: 'translateY(-50%)'}} />
                  <select 
                    className="form-select form-select-sm border bg-light ps-4 fw-bold text-secondary shadow-none"
                    style={{fontSize: '12px', width: '130px', cursor: 'pointer'}}
                    value={globalFolder}
                    onChange={(e) => setGlobalFolder(e.target.value)}
                  >
                    <option value="ALL">All Folders</option>
                    <option value="INBOX">Inbox Only</option>
                    <option value="SPAM">Spam Only</option>
                  </select>
              </div>

              {/* Global search*/}
              <div className="position-relative" style={{width: '280px'}}>
                  <Search size={14} className="text-muted position-absolute" style={{left: '10px', top: '50%', transform: 'translateY(-50%)'}} />
                  <input 
                    type="text" 
                    className="form-control form-control-sm border bg-light ps-4 shadow-none"
                    placeholder="Global Search (Subject, Sender, IP)..."
                    style={{fontSize: '12px'}}
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                  />
              </div>

              <div className="vr mx-1 text-muted opacity-25"></div>

              <button className="btn btn-sm btn-white border text-secondary" onClick={handleRefreshAll} title="Refresh All">
                <RotateCcw size={14} />
              </button>

              <button className={`btn btn-sm ${globalCopied ? 'btn-success' : 'btn-dark'} d-flex align-items-center gap-2 px-3`} onClick={handleCopyAllIps}>
                {globalCopied ? <Check size={14} /> : <Copy size={14} />}
                <span>Copy IPs</span>
              </button>
          </div>
      </div>
      
      <div className="p-3">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="row">
            <div className="col-12">
                {accounts.map((acc, index) => (
                  <div 
                    key={acc.email}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className="d-flex align-items-start gap-1 mb-1 drag-row"
                  >
                    <div 
                      className="mt-2 drag-handle" 
                      style={{ cursor: 'grab', color: 'var(--danger)' }}
                      title="Drag to reorder"
                    >
                      <GripVertical size={18} />
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <BoxRow 
                          ref={el => boxRefs.current[index] = el}
                          email={acc.email} 
                          metadata={acc} 
                          globalFilter={globalSearch}
                          globalFolder={globalFolder}
                          onGlobalFilterChange={setGlobalSearch}
                      />
                    </div>
                  </div>
                ))}
            </div>
        </div>
      </div>

    </div>
  );
};

export default SharedMassPage;